import { Camera, Info } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { FaCloudUploadAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useParallelUpload } from "../../hooks/useParallelUpload";
import { useQuotaCheck } from "../../hooks/useQuotaCheck";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../listings/slideshow/Tooltip";
import { type DragAndDropMediaProps, UploadStatus } from "../listings/types";
import FeatureItem from "../onboarding/FeatureItem";
import { Button } from "./Button";
import CameraSettingsOverlay from "./CameraOverlay";
import VideoRecorderModal from "./VideoRecorderModal";
import CustomToast from "./CustomToast";
import PlanLimitReached from "./PlanLimitReached";
import UploadQueue from "./UploadQueue";
import VideoProcessingMessage from "./VideoProcessingMessage";


interface FileWithPreview {
    id: string;
    file: File;
    preview: string;
}

interface MediaError {
    type: string;
    message: string;
    filename?: string;
}

const MAX_VIDEO_SIZE = 2 * 1024 ** 3; // 2 GB in bytes
const MAX_IMAGE_SIZE = 25 * 1024 * 1024; // 25 MB in bytes
const MAX_VIDEO_DURATION = 125; // 2 minutes 5 seconds in seconds

const VIDEO_EXTENSIONS = new Set(["mp4", "mov", "mkv", "webm", "avi", "matroska"]);

// Some browsers (Chrome on macOS, Firefox) don't populate file.type for
// certain containers (e.g. .mkv). Fall back to extension detection.
const isVideoFile = (file: File): boolean => {
    if (file.type.startsWith("video/")) return true;
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    return VIDEO_EXTENSIONS.has(ext);
};

const getVideoDuration = (file: File): Promise<number> =>
    new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const video = document.createElement("video");
        video.preload = "metadata";
        video.onloadedmetadata = () => {
            URL.revokeObjectURL(url);
            resolve(video.duration);
        };
        video.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error("Could not read video metadata"));
        };
        video.src = url;
    });

// Feature flag for parallel uploads - can be controlled via environment variable

const DragAndDropMedia: React.FC<DragAndDropMediaProps> = ({
    type,
    listingId,
    onUploadStart,
    onUploadComplete,
    propertyAddress1,
    mlsApproved,
    assetType,
    isUploading,
    setIsUploading,
    listingType,
    isAdhoc = false,
    onPendingFilesChange,
    isSyncing = false,
    hideNotes = false,
    hideRenewalNote = false,
    onActiveUploadsChange,
    isActive = true,
    existingMedia,
    onImageTagLimitExceeded,
}) => {
    const [files, setFiles] = useState<FileWithPreview[]>([]);
    const [videosNeedProcessing, setVideosNeedProcessing] = useState(false);
    const [processingMessageDismissed, setProcessingMessageDismissed] =
        useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const [showSettingsOverlay, setShowSettingsOverlay] = useState(false);
    const [showRecorder, setShowRecorder] = useState(false);

    const isMobile = useIsMobile(700);
    const isMobileDevice =
        typeof navigator !== "undefined" &&
        /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    const [planLimit, setPlanLimit] = useState(false);

    const { fetchQuotaStatus, checkMultipleFilesQuota } = useQuotaCheck();

    // Load quota status on component mount
    useEffect(() => {
        fetchQuotaStatus();
    }, [fetchQuotaStatus]);

    // Memoized callbacks to prevent infinite rerenders
    const handleParallelUploadComplete = useCallback(() => {
        onUploadComplete?.();
        setFiles([]);
        onPendingFilesChange?.(0);
        // Refresh quota status after successful upload
        fetchQuotaStatus();
    }, [onUploadComplete, onPendingFilesChange, fetchQuotaStatus]);

    // Parallel upload system
    const parallelUpload = useParallelUpload({
        type,
        listingId,
        listingType,
        propertyAddress1,
        assetType,
        onUploadStart,
        onUploadComplete: handleParallelUploadComplete,
        onImageTagLimitExceeded,
        existingMedia,
    });

    // Clean up object URLs to prevent memory leaks
    useEffect(() => {
        return () => {
            files.forEach((fileWithPreview) =>
                URL.revokeObjectURL(fileWithPreview.preview)
            );
        };
    }, [files]);

    // Store active upload state for refresh detection
    const hasInitiallyMounted = useRef(false);
    useEffect(() => {
        if (parallelUpload.hasActiveUploads) {
            sessionStorage.setItem('uploadsInterrupted', 'true');
        } else if (hasInitiallyMounted.current) {
            // Only remove the flag if uploads completed naturally (not on initial mount)
            sessionStorage.removeItem('uploadsInterrupted');
        }
        hasInitiallyMounted.current = true;
    }, [parallelUpload.hasActiveUploads]);

    // Keep pendingUploads in sync with actual queue state
    // This handles cases where files are removed or all uploads fail
    useEffect(() => {
        const nonCompletedCount = parallelUpload.queueState.files.filter(
            (f) => f.status !== UploadStatus.COMPLETED
        ).length;
        onPendingFilesChange?.(nonCompletedCount);
    }, [parallelUpload.queueState.files, onPendingFilesChange]);

    // Notify parent of active uploads count changes
    useEffect(() => {
        if (onActiveUploadsChange) {
            const activeCount = parallelUpload.queueState.activeImageUploads + parallelUpload.queueState.activeVideoUploads;
            
            // Only report actual count if this tab is active, otherwise report 0
            const reportedCount = isActive ? activeCount : 0;
            
            onActiveUploadsChange(reportedCount);
        }
    }, [parallelUpload.queueState.activeImageUploads, parallelUpload.queueState.activeVideoUploads, onActiveUploadsChange, isActive]);

    const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleFiles = useCallback(
        async (selectedFiles: File[]) => {
            // Check quota before processing files
            const quotaCheck = await checkMultipleFilesQuota(selectedFiles, type);

            if (!quotaCheck.canUpload) {
                setPlanLimit(true);
                return;
            }

            // Use parallel upload system if enabled
            // Show any immediate errors for invalid files
            const invalidFiles: MediaError[] = []; // format errors (images/non-video) → toast
            const validFiles: File[] = [];

            for (const file of selectedFiles) {
                const isImage =
                    file.type.startsWith("image/") && file.type !== "image/gif";
                const isVideo = isVideoFile(file);

                // Basic validation before adding to parallel upload queue
                let isValid = false;
                let errorMessage = "";
                let failInQueue = false; // video limit failures go into the queue UI

                switch (type) {
                    case "image":
                    case "logo":
                    case "assetImage":
                        if (!isImage) {
                            errorMessage =
                                "Unsupported file format. Please upload PNG, JPEG, or WebP images.";
                        } else if (file.size > MAX_IMAGE_SIZE) {
                            errorMessage = "Exceeds maximum size of 25 MB";
                        } else {
                            isValid = true;
                        }
                        break;

                    case "video":
                    case "assetVideo":
                        if (!isVideo) {
                            errorMessage =
                                "Unsupported file format. Please upload MP4, MOV, or other video formats.";
                        } else if (file.size > MAX_VIDEO_SIZE) {
                            errorMessage = "Exceeds maximum size of 2 GB";
                            failInQueue = true;
                        } else {
                            try {
                                const duration = await getVideoDuration(file);
                                if (duration > MAX_VIDEO_DURATION) {
                                    const mins = Math.floor(duration / 60);
                                    const secs = Math.round(duration % 60);
                                    errorMessage = `Video duration (${mins}m ${secs}s) exceeds the maximum allowed length of 2 minutes 5 seconds.`;
                                    failInQueue = true;
                                } else {
                                    isValid = true;
                                }
                            } catch {
                                // Can't read metadata — let the backend validate
                                isValid = true;
                            }
                        }
                        break;

                    case "asset":
                    case "listing":
                        if (!isImage && !isVideo) {
                            errorMessage =
                                "Unsupported file format. Please upload images or videos.";
                        } else if (isVideo && file.size > MAX_VIDEO_SIZE) {
                            errorMessage = "Exceeds maximum video size of 2 GB";
                            failInQueue = true;
                        } else if (isImage && file.size > MAX_IMAGE_SIZE) {
                            errorMessage =
                                "Exceeds maximum image size of 25 MB";
                        } else if (isVideo) {
                            try {
                                const duration = await getVideoDuration(file);
                                if (duration > MAX_VIDEO_DURATION) {
                                    const mins = Math.floor(duration / 60);
                                    const secs = Math.round(duration % 60);
                                    errorMessage = `Video duration (${mins}m ${secs}s) exceeds the maximum allowed length of 2 minutes 5 seconds.`;
                                    failInQueue = true;
                                } else {
                                    isValid = true;
                                }
                            } catch {
                                isValid = true;
                            }
                        } else {
                            isValid = true;
                        }
                        break;
                }

                if (isValid) {
                    validFiles.push(file);
                } else if (failInQueue) {
                    // Video size/duration failures → show in queue UI as a failed item
                    parallelUpload.addFailedFile(file, errorMessage);
                } else {
                    invalidFiles.push({
                        type: "validation",
                        message: errorMessage,
                        filename: file.name,
                    });
                }
            }

            // Show toasts only for format errors (image/non-video issues)
            if (invalidFiles.length > 0) {
                invalidFiles.forEach((error) => {
                    toast.error(
                        <CustomToast
                            message={`${error.filename}: ${error.message}`}
                            type="error"
                        />
                    );
                });
            }

            // Add valid files to parallel upload queue
            if (validFiles.length > 0) {
                parallelUpload.addFiles(validFiles);
                onPendingFilesChange?.(
                    parallelUpload.queueState.files.length + validFiles.length
                );
            }
        },
        [type, onPendingFilesChange, checkMultipleFilesQuota, parallelUpload]
    );

    const dismissProcessingMessage = () => {
        setProcessingMessageDismissed(true);
    };

    const onDrop = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();
            const droppedFiles = Array.from(e.dataTransfer.files);
            void handleFiles(droppedFiles);
        },
        [handleFiles]
    );

    const handleButtonClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            void handleFiles(Array.from(e.target.files));
            // Reset the input value to allow uploading the same file again if needed
            e.target.value = "";
        }
    };

    // Open the video processing overlay when user clicks on "Needs Processing"
    const openProcessingOverlay = useCallback(() => {
        setProcessingMessageDismissed(false);
        setVideosNeedProcessing(true);
    }, []);

    return (
        <>
            {planLimit && (
                <PlanLimitReached
                    open={planLimit}
                    onClose={() => setPlanLimit(false)}
                    type="storage"
                />
            )}
            <div
                className={`w-full relative mx-auto py-4 ${
                    isMobile ? "pt-6 px-0" : "pt-8 p-8"
                }`}
            >
                {/* Video Processing Overlay */}
                {videosNeedProcessing &&
                    !processingMessageDismissed &&
                    (type === "video" ||
                        type === "assetVideo" ||
                        type === "asset" ||
                        type === "listing") && (
                        <VideoProcessingMessage
                            dismissProcessingMessage={dismissProcessingMessage}
                        />
                    )}

                {/* Camera Settings Suggestion - only for video uploads */}
                {(type === "video" || type === "assetVideo" || type === "listing") && (
                    <div className="bg-gray-100 p-3 rounded-md mb-4 flex flex-row justify-between items-start">
                        <p className="text-sm text-red-500">
                            <strong className="text-primary">Note: </strong> Use <strong className="text-red-500">Record Video</strong> below to capture new footage. The camera option inside <strong className="text-red-500">Select Files</strong> records at low resolution and uploads will fail.
                        </p>
                        <div className="flex-shrink-0 self-start">
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            className="h-5 w-5 p-0 text-primary hover:text-black hover:bg-transparent"
                                            onClick={() =>
                                                setShowSettingsOverlay(true)
                                            }
                                            tabIndex={-1}
                                        >
                                            <Info className="h-5 w-5" />
                                            <span className="sr-only">
                                                Default Camera Settings
                                            </span>
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent className="text-xs text-white">
                                        Click here for Camera Settings Guide.
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                    </div>
                )}

            {showSettingsOverlay && (
                <div className="relative h-full flex items-center">
                    <CameraSettingsOverlay
                        onClose={() => {
                            setShowSettingsOverlay(!showSettingsOverlay);
                        }}
                    />
                </div>
            )}

            {showRecorder && (
                <VideoRecorderModal
                    onRecordingComplete={(file) => {
                        setShowRecorder(false);
                        void handleFiles([file]);
                    }}
                    onClose={() => setShowRecorder(false)}
                    enforceAspectRatio={!isMobileDevice}
                />
            )}

          {/* Drag and Drop Area */}
<div
    onDragOver={(e) => {
        if (isSyncing) return;
        onDragOver(e);
    }}
    onDrop={(e) => {
        if (isSyncing) return;
        onDrop(e);
    }}
    className={`border-2 border-dashed rounded-lg p-6 sm:p-8 text-center cursor-pointer hover:border-gray-400 transition-colors px-4 ${
        isSyncing ? "pointer-events-none opacity-50" : ""
    }`}
    onClick={handleButtonClick}
    tabIndex={0}
    role="button"
    aria-label={`Drag and drop area for ${
        type === "image" || type === "assetImage"
            ? "images"
            : type === "video" || type === "assetVideo"
            ? "videos"
            : type === "asset" || type === "listing"
            ? "images and videos"
            : type === "logo"
            ? "logos"
            : "media"
    }`}
    onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleButtonClick();
        }
    }}
>
    <FaCloudUploadAlt
        className="mx-auto text-secondary mb-4"
        size={isMobile ? 36 : 48} // Adjust icon size based on device
    />
    <p
        className={`text-primary mb-2 ${
            isMobile ? "text-base" : "text-lg"
        }`}
    >
        Drag and drop your{" "}
        {type === "image" || type === "assetImage"
            ? "images"
            : type === "video" || type === "assetVideo"
            ? "videos"
            : type === "asset" || type === "listing"
            ? "images and videos"
            : type === "logo"
            ? "logos"
            : "media"}{" "}
        here
    </p>
    <p
        className={`text-primary text-sm ${
            isMobile ? "text-xs" : ""
        }`}
    >
        or
    </p>
    {(type === "video" ||
        type === "assetVideo" ||
        type === "listing" ||
        type === "asset") ? (
        <div className="flex flex-col items-center gap-2 mt-2">
            <Button
                id="record-video-button"
                type="button"
                size="sm"
                className="flex items-center gap-1.5 w-36"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    setShowRecorder(true);
                }}
            >
                <Camera className="h-4 w-4" />
                Record Video
            </Button>
            <Button
                id="select-files-button"
                type="button"
                size="sm"
                className="w-36"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleButtonClick();
                }}
            >
                Select Files
            </Button>
        </div>
    ) : (
        <Button
            id="select-files-button"
            type="button"
            size="sm"
            className="mt-2 w-36"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                handleButtonClick();
            }}
        >
            Select Files
        </Button>
    )}
    <input
    id="media-upload-input"
    ref={fileInputRef}
    type="file"
    multiple
    accept={
        type === "image" || type === "assetImage"
            ? "image/png, image/jpeg, image/webp"
            : type === "video" || type === "assetVideo"
            ? "video/*"
            : type === "asset" || type === "listing"
            ? "image/png, image/jpeg, image/webp, video/*"
            : "image/png, image/jpeg, image/webp, video/*"
    }
    className="hidden"
    onChange={handleFileInputChange}
    aria-label="File input"
/>
</div>

{/* File size limits and quota information */}
<div className="mt-2 space-y-2">
    <div className="text-sm text-gray-500">
        {(type === "image" || type === "assetImage") && (
            <p>Maximum image size: 25 MB per file.</p>
        )}
        {(type === "video" ||
            type === "assetVideo" ||
            type === "asset" ||
            type === "listing") && (
            <p>
                Maximum video size: 2 GB per file and 2 minutes
                in duration
            </p>
        )}
    </div>
</div>

{/* Parallel Upload Queue */}
{parallelUpload.queueState.files.length > 0 && (
    <UploadQueue
        queueState={parallelUpload.queueState}
        onRemoveFile={parallelUpload.removeFile}
        onRetryFile={parallelUpload.retryFile}
        onCancelFile={parallelUpload.cancelFile}
        onClearCompleted={parallelUpload.clearCompleted}
        onShowProcessingOverlay={openProcessingOverlay}
        showCompactView={false}
    />
)}

                {!hideNotes && (
                    <div className="bg-gray-100 p-3 rounded-md mt-4">
                        <p className="text-sm text-primary font-bold">Note:</p>
                        <div className="text-primary text-sm flex flex-col">
                            {(isAdhoc || !hideRenewalNote) && (
                                <FeatureItem
                                    size="sm"
                                    text={
                                        isAdhoc
                                            ? "New media will be added to Shared Media, while Campaign Media will remain unchanged."
                                            : "New media will only take effect with the next campaign renewal."
                                    }
                                />
                            )}
                            {!isAdhoc ? (
                                <>
                                    <FeatureItem
                                        size="sm"
                                        text="Deleting media cannot be undone."
                                    />
                                    <FeatureItem
                                        size="sm"
                                        text="Deleted media will remain available in current campaigns but cannot be used in future renewals."
                                    />
                                </>
                            ) : null}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default DragAndDropMedia;